import React from 'react';
import Card from 'react-bootstrap/Card';
import { AiOutlineDown, AiOutlineUp } from 'react-icons/ai';
import classNames from 'classnames';
import HeadTitle from '../components/head_title';
import Layout from '../components/layout/layout';
import Breadcrumbs from '../components/breadcrumbs';
import { genericTextChange } from '../utils/forms';
import ReactDOMServer from 'react-dom/server';
import {kebabCase} from "lodash";

interface FAQSection {
  title: string;
  items: FAQSectionItems[];
}

interface FAQSectionItems {
  title: string;
  content: React.ReactNode;
}

const faq: FAQSection[] = [
  {
    title: 'General',
    items: [
      {
        title: 'What is Indo4ward?',
        content: (
          <div>
            Indo4ward works with merchants in Indonesia and forwards your purchases in Indonesia outwards to you. All you have
            to do is to:
            <ol>
              <li>Find the product link from an Indonesia online store</li>
              <li>Paste the link on our platform</li>
              <li>Indicate the product variation or instructions needed for us to buy them</li>
              <li>Pay</li>
              <li>Receive your order</li>
            </ol>
            <img src="/images/faq/faq-1.png" alt="faq-1" className="d-flex d-lg-none w-100" />
            <img src="/images/faq/faq-1.png" alt="faq-1" className="d-none d-lg-flex w-50" />
          </div>
        )
      },
      {
        title: 'Where can I shop for Indonesia products?',
        content: (
          <div>
            There are so much to shop from in Indonesia, but here are a few marketplace to start from:
            <ul>
              <li>Tokopedia (All types of item) @ <a href="https://www.tokopedia.com">www.Tokopedia.com</a></li>
              <li>Orami (Baby and Mother Care) @ <a href="https://www.orami.co.id">www.Orami.co.id</a></li>
              <li>Shopee ID (All types of item) @ <a href="https://www.shopee.co.id">www.Shopee.co.id</a></li>
              <li>Bukalapak (All types of item) @ <a href="https://www.bukalapak.com">www.Bukalapak.com</a></li>
              <li>Lazada (All types of item) @ <a href="https://www.lazada.co.id">www.lazada.co.id</a></li>
              <li>Blibli (All types of item) @ <a href="https://www.blibli.com">www.blibli.com</a></li>
              <li>Carousell (Preloved items) @ <a href="https://id.carousell.com">id.carousell.com</a></li>
            </ul>
            Of course, you don't have to just choose the above stores. We're opened to any online shops in Indonesia! Simply paste the link on our homepage and get started!
          </div>
        )
      },
      {
        title: 'How does Indo4ward help me with my purchase in Indonesia?',
        content: (
          <div>
            Indo4ward works with multiple shipping vendors in Indonesia to ensure a competitive prices with the assurance and quality service of being an international marketplace for all Indonesia products.<br />
            When a customer makes a purchase with us on Indo4ward, we will broker with the seller to purchase the products which will be sent to the selected warehouse. Once a product is checked for accuracy, we will ship it directly from Indonesia.<br />
            <br />
            In the case where a product is incorrect, we will process them for a return and seek recourse with the seller. Should the seller cannot provide a resolution, we will process your order for refund at no cost to you.<i>Yes, all these are handled by us. So rest assure when you shop in Indonesia!</i>
          </div>
        )
      },
      {
        title: 'How do I reset my password?',
        content: (
          <div>
            In the case where you do forget your password:
            <ol>
              <li>Click on the Sign In button</li>
              <li>Click on the “Forget password”</li>
            </ol>
            You will be prompted to enter your email address. Follow the instructions to complete the password reset.
          </div>
        )
      },
      {
        title: 'Can I purchase items more than 30kg?',
        content: (
          <div>
            Of course! However, do note that products that's bigger than 30kg might need our staff's assistance in getting the order through.<br />
            Please reach out to us at <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>
          </div>
        )
      },
      {
        title: 'What are Indo4ward operating hours?',
        content: (
          <div>
            We operate from Monday to Friday from 09:00 to 18:00 Singapore Time.
          </div>
        )
      }
    ]
  },
  {
    title: 'Ordering',
    items: [
      {
        title: 'When will my order be processed (a.k.a. purchased) for me?',
        content: (
          <div>
            We aim to process all our orders within 24-48 hours within business operational days. However, do note that at times public holiday may affect the duration required to process your order.
          </div>
        )
      },
      {
        title: 'Can I purchase products from different merchants or online shop and consolidate them in one order?',
        content: (
          <div>
            Yes, that's the brilliance of Indo4ward. You can simply accumulate all your items in your shopping cart.<br />
            Be it through quotation or integrated marketplaces (e.g., Tokopedia or Orami), and have them checked out all in one order!
          </div>
        )
      },
      {
        title: 'Why is my order cancelled when there\'s still stocks on Indo4ward?',
        content: (
          <div>
            There can be many reason why an order is cancelled when there's still stocks listed on Indo4ward.<br />
            This is mainly because the stocks listed on Indo4ward is an hourly representation of the actual seller's listing, and because of this your order can be cancelled because:<br />
            <ol>
              <li>Information on Indo4ward has yet to be updated with the new stock counts from the seller's listing</li>
              <li>Seller might have already removed the listing, and has yet been removed by Indo4ward</li>
              <li>Seller did not update their stocks listed, and only when purchased do they inform us that they do not have stock</li>
            </ol>
            However, in the case of a seller not being able to fulfil your order, we will cancel and refund your orders accordingly.
          </div>
        )
      },
      {
        title: 'Why is my order cancelled when there\'s still stocks on Tokopedia?',
        content: (
          <div>
            If you have checked that Tokopedia did indeed have stocks, but wondered why your order was still cancelled, it is likely because of the following:<br />
            <ol>
              <li>Seller might not have responded to the orders sent to them, and it has gone past the required timeline to fulfil the order</li>
              <li>Seller might have informed us that the variant or stock is not longer available</li>
            </ol>
            While it is unfortunate for your order gets cancelled, this happens frequently as Indonesia sellers don't often manage their listing on a frequent basis.<br />
            Read this article to find out more on how you can avoid this: <a href="https://blog.indo4ward.com/spotting-the-problematic-sellers-online-and-prevent-your-order-from-being-cancelled-iffy-product-listings-on-indonesia-marketplace-tokopedia-shopee-part-1/">Click here</a>
          </div>
        )
      },
      {
        title: 'What if my items does not look like the picture or description provided by the Seller?',
        content: (
          <div>
            Depending on the items' condition that was purchased, you may or may not entitle any refunds or returns for products that might differ from the listed photos by the seller.<br />
            However, we will do our best to reach out to the seller and resolve the matter to the best of our ability.<br /><br />

            Kindly please reach out to us @ <a href="mailto:info@indo4ward.com">info@indo4ward.com</a> with the following details:
            <ul>
              <li>Order number</li>
              <li>Date of receival</li>
              <li>Photos of the product</li>
              <li>Video of unboxing</li>
            </ul>
          </div>
        )
      },
      {
        title: 'I have an order that needs urgent attention, what can I do?',
        content: (
          <div>
            We understand that there are orders that needs to be processed on a time sensitive manner.<br />
            While we cannot guarantee any of such services as Indo4ward process orders on a batch basis.<br />
            <br />
            You can make attempts to leave a note on your order and drop us an email @ <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>
          </div>
        )
      },
      {
        title: 'What happens if one of my item gets cancelled in my order?',
        content: (
          <div>
            When an item in an order gets cancelled, the order will still continue unless all items failed to be fulfilled by the sellers.<br />
            This is because once an item has been processed and purchased from the seller, it cannot be cancelled as the item is already on its way to our warehouse.
          </div>
        )
      }
    ]
  },
  {
    title: 'Quotations',
    items: [
      {
        title: 'What are quotations?',
        content: (
          <div>
            Quotations is a system that allows Indo4ward to quote products that are not able to be quoted in realtime through integrated marketplaces (i.e., Tokopedia and Orami).<br />
            As such, in order for Indo4ward to fulfil products outside of integrated marketplace, we have devised a way to quote products from any Indonesia marketplaces.
          </div>
        )
      },
      {
        title: 'What are the details I need to include in my quotations for Indo4ward?',
        content: (
          <div>
            When submitting your quotation, it is important to note if there are any variations to your product.<br />
            If there are any variations to the product, please note them down on the instructions so that we could review your quotations at the quickest possible manner. (See image below)<br />
            <img src="/images/faq/faq-quotations-instructions.png" alt="FAQ-quotations" className="d-flex d-lg-none w-100" /> <br />
            <img src="/images/faq/faq-quotations-instructions.png" alt="FAQ-quotations" className="d-none d-lg-flex w-50" />
          </div>
        )
      },
      {
        title: 'Why am I redirected to quotation when pasting a marketplace link?',
        content: (
          <div>
            While we are doing our best to integrate as many Marketplaces as we can, we unfortunately have yet integrated the
            marketplace you have provided us. But fret not, we still want to help you! <br />
            <br />
            Hence, all orders from 3rd Party Marketplaces or Online Store will be processed and quoted manually within 2 business days! <br />

            The process to manual quotation is simply as follows:
            <ol>
              <li>You will provide us the links to the items you would like to purchase.</li>
              <li>We will provide you a quotation within 2 business days</li>
              <li>Once a quotation is issued to you, you will have the ability to add the items to cart for checkout just like any other items!</li>
            </ol>
          </div>
        )
      },
      {
        title: 'How does Indo4ward review prices that were from quotation?',
        content: (
          <div>
            Whenever a customer submits a quotation via our website, we will take roughly 2 business days to assess the product pricing that can be suitable for shipping.<br />
            After which, Indo4ward will quote you with the prices where you could simply add it to your cart and checkout straight from the platform.
          </div>
        )
      }
    ]
  },
  {
    title: 'Pricings & Payments',
    items: [
      {
        title: 'How does Indo4ward calculate its prices and charges of a product?',
        content: (
          <div>
            All orders are charged according to product's:
            <ul>
              <li>Price</li>
              <li>Weight</li>
              <li>Size</li>
              <li>Category</li>
              <li>Destination</li>
            </ul>
            However, should a product be overly inflated, you can also dispute them through the carts page. (See image below)<br />
            <img src="/images/faq/faq-price-review.png" alt="faq-price-review" className="d-flex d-lg-none w-100" /><br />
            <img src="/images/faq/faq-price-review.png" alt="faq-price-review" className="d-none d-lg-flex w-50" />
          </div>
        )
      },
      {
        title: 'Are the prices listed on Indo4ward inclusive of shipping to my doorstep?',
        content: (
          <div>
            Yes. The prices listed on Indo4ward is inclusive of the bulk of the shipping cost. This is also the reason why the prices are much more inflated as compared to the originatin online shop.<br />
            <br />
            However, do note that the prices does not include a flat order handling fee that can be seen at the checkout page.
          </div>
        )
      },
      {
        title: 'What are the payment methods that I can use?',
        content: (
          <div>
            We currently accept all international cards via PayPal and other payment methods (for Singapore customers only).
          </div>
        )
      },
      {
        title: 'How do I use my vouchers?',
        content: (
          <div>
            Vouchers can be applied during checkout. Do note that only 1 voucher at any time can be applied to the order.
          </div>
        )
      },
      {
        title: 'Are my Payment details and data secure?',
        content: (
          <div>
            We do not hold any payment data on our platform. All card information is being held by our internationally renown payment providers for processing only.
            We respect your privacy therefore any data provided by You to Us will be securely stored.
          </div>
        )
      },
      {
        title: 'Will there be any Custom fee (GST/Taxes/Duties) charged?',
        content: (
          <div>
            Unless explicitly stated, all customs and duties are the responsibility of the customer.
            Indo4ward does not in any way have the ability to guarantee the payment of additional charges arised at the destination.
          </div>
        )
      },
      {
        title: 'Why am I being prompt to pay additional fee for my order?',
        content: (
          <div>
            While this is not very common for us to do, it does happens when we notice a huge discrepancies on products that were incorrect declared by the seller (i.e., Product dimensions, Remote area deliveries).<br />
            Because Indo4ward does not own any products, the only fees we charge are mainly the shipping cost and service fees that's included in the product.<br />
            <br />

            However, when a product is incorrectly listed by the seller and out of our company policy's margin, we will inform the customer of the excess.<br />
            Should a customer refuse to proceed with the excess charges, we will cancel and refund our customer of the order to safeguard all parties in the transaction.
          </div>
        )
      },
      {
        title: 'Why is Indo4ward more expensive than what is listed on the marketplace?',
        content: (
          <div>
            Indo4ward is an aggregator-forwarder, and we are not the seller. Indo4ward enables customers all around the world to purchase from Indonesia in a safe and assuring way.
            Prices listed on Indo4ward is inclusive of shipping and service charges (excluding the order handling fee that can be seen at the checkout page).<br />
            <br />
            Prices that are usually inflated is likely because the product is bigger than it should, and because we charge on the standards of <b>Volumetric weight</b>, products are often are more expensive than what is listed on the originating marketplace.
          </div>
        )
      },
      {
        title: 'Can I request an invoice?',
        content: (
          <div>
            Yes! You can view and screenshot your order page, which contains a full breakdown of your payment and is suitable for most administrative needs.<br />

            If you need a PDF version, just submit a request—we'll provide one as soon as possible. Please note that the PDF will reflect the same details shown on your order dashboard.<br />

            For customs or declaration purposes, if you require the original seller's invoice, please contact us directly. In most cases, a screenshot of your order is sufficient, but we're happy to assist if you need more.
          </div>
        )
      },
    ]
  },
  {
    title: 'Shipping & Delivery',
    items: [
      {
        title: 'Where can Indo4ward ship their orders to?',
        content: (
          <div>
            You will be able to identify the list of countries we're able to to ship to via the top right corner of our platform.
            Should you want to ship to a country we do not have on the list, feel free to reach out to us at <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>
          </div>
        )
      },
      {
        title: 'What are the items that cannot be shipped?',
        content: (
          <div>
            The following categories of items are not allowed to be shipped by Indo4ward:
            <ul>
              <li>Batteries or containing batteries</li>
              <li>Vape & Tobacco products</li>
              <li>Alcohol</li>
              <li>Currencies or any form of counterfeit currencies</li>
              <li>Embargoes goods</li>
              <li>Firearms and weapons</li>
              <li>Animal and wildlife products</li>
              <li>Obscene, seditious or treasonous materials</li>
              <li>Prescription-only, pharmacy-only medicinal products</li>
              <li>Human parts or remains</li>
            </ul>
            Indo4ward will reserve the rights to cancel and refund orders that are unable to be processed accordingly.
          </div>
        )
      },
      {
        title: 'How do I update my address information?',
        content: (
          <div>
            Updating your address can only be done after logging in.
            <ol>
              <li>Go to your account detail</li>
              <li>Click on Manage Address</li>
              <li>Choose the address that you want to update</li>
              <li>Click on Edit and you can update your address.</li>
            </ol>
          </div>
        )
      },
      {
        title: 'How long will an order take to reach me?',
        content: (
          <div>
              <p>The total delivery time is calculated by adding our <strong>processing time</strong> to the <strong>shipping
                  duration</strong> based on your location.</p>
              <ul>
                  <li><p><strong>Processing Time:</strong> It takes <strong>6-10 business days</strong> for us to source your item
                      and prepare it for shipment from our warehouse.</p>
                  </li>
                  <li><p><strong>Shipping Duration:</strong> Once shipped, estimated delivery times are:</p>
                      <ul>
                          <li><p><strong>Asia:</strong> Approximately 5 business days.</p>
                          </li>
                          <li><p><strong>All Other Destinations:</strong> Approximately 7 business days.</p>
                          </li>
                      </ul>
                  </li>
              </ul>
              <p><strong>Summary:</strong> Most customers receive their orders within <strong>11-17 business days</strong> total.</p>

          </div>
        )
      },
      {
        title: 'Why does it say my address is invalid even though Indo4ward ships to my country?',
        content: (
          <div>
            There are two common reasons this might happen:
            <ul>
              <li><b>Incorrect region/country selected</b>: Make sure you've selected the correct region using the dropdown at the top right corner of the screen.</li>
              <li><b>Shipping not yet supported on platform</b>: In some cases, our platform may not yet support automated checkout for your location. If this happens, feel free to reach out to us—we'll do our best to assist you manually.</li>
            </ul>
            If you don't fall under any of the scenarios above, please reach out to us and we'll do our best to help you!
          </div>
        )
      },
      {
        title: 'How do I track my orders?',
        content: (
          <div>
            All orders will have a “Journey Log” that gives you visibility on where your order is currently
            at. <br />
            <br />
            You can refer to these updates via Account Details &gt; My Orders &gt; View Orders <br />
            <img src="/images/faq/faq-14.png" alt="faq-14" className="d-flex d-lg-none w-100" />
            <img src="/images/faq/faq-14.png" alt="faq-14" className="d-none d-lg-flex w-50" /> <br />

            As soon as orders that have been shipped, Indo4ward will be provide you with a tracking number. Tracking can be done in 3 areas:
            <ol>
              <li><a href="https://t.me/indo4wardbot">Indo4ward's Telegram Bot</a></li>
              <li><a href="https://tracking.indo4ward.com/">Indo4ward Tracking site</a></li>
              <li>Selected order that uses <a href="https://www.dhl.com/sg-en/home/tracking.html">DHL Express</a></li>
              <li>Selected order that uses <a href="https://www.fedex.com/en-sg/tracking.html">FedEx</a></li>
              <li>Selected order that uses <a href="https://www.ups.com/track?loc=en_SG&requester=ST/">UPS</a></li>
            </ol>

            Should you have any issues with tracking your order, you can simply chat with us via the support chat located at the bottom of an order or email us at: <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>
          </div>
        )
      },
      {
        title: 'Where does Indo4ward ship all their orders from??',
        content: (
          <div>
            All orders are fulfiled directly from Indonesia.
          </div>
        )
      },
      {
        title: 'Can we send food over to Singapore?',
        content: (
          <div>
            Foods with liquid amenities, sauces, and wet food are not allowed. However, Dry Foods such as Snacks, Crackers,
            Paste sauce (securely packed and contained) & other kinds of foods that will have not probability of spilling or
            spoiling through the shipping process will likely to be accepted for shipment.
          </div>
        )
      },
      {
        title: 'Why can I only track my order on the Indo4ward platform?',
        content: (
          <div>
            Indo4ward uses a combination of shipping partners to keep costs low while ensuring reliable and timely delivery. Because we optimize each shipment based on the most efficient vendor at the time, tracking is consolidated and made available directly on our platform for your convenience.<br />

            In some cases, a vendor-specific tracking number may be issued, but this varies depending on how the order is fulfilled.
          </div>
        )
      },
    ]
  },
  {
    title: 'Merchants & Partnerships',
    items: [
      {
        title: 'Is Indo4ward open to new business opportunities?',
        content: (
          <div>
            Of course, Indo4ward is constantly in the look out on how we can improve Indonesia's business reach across the world. <br />
            If you have an idea, please reach us at <a href="mailto:info@indo4ward.com">info@indo4ward.com</a> and we'll get back to you as soon as we can!
          </div>
        )
      }, {
        title: 'I run an Indonesian brand, can Indo4ward help me with getting my order abroad?',
        content: (
          <div>
            Indo4ward doesn't just fulfil orders of our own, but we also provide fulfilment services for brand owners in Indonesia exclusively. <br />
            If you have an order that's located outside of Indonesia, we're happy to offer our Business rates to you! For more details, please reach out to us at <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>!
          </div>
        )
      },
      {
        title: 'Does Indo4ward fulfill bulk orders for businesses?',
        content: (
          <div>
            Yes, we can work something out and provide our expertise.
            Please reach us at <a href="mailto:info@indo4ward.com">info@indo4ward.com</a>!
          </div>
        )
      }, {
        title: 'Are we able to list our products on Indo4ward?',
        content: (
          <div>
            If you're an official and authorized seller, you can list your products on Indo4ward as a merchant.<br />
            However, if you're an individual reseller, we have yet opened up a campaign for you. So do keep an eye on Indo4ward.
          </div>
        )
      }, {
        title: 'Does Indo4ward help promote Indonesia products and brands overseas?',
        content: (
          <div>
            While we recommend that brand focuses on marketing their brands overseas, Indo4ward will frequently feature their merchants' brand on their platform and social media channels.
          </div>
        )
      },]
  }
];

// Function to extract text from ReactElement
function getTextFromReactElement(element: React.ReactNode): string {
  if (typeof element === 'string') {
    return element;
  }

  if (React.isValidElement(element)) {
    return ReactDOMServer.renderToStaticMarkup(element);
  }

  return '';
}

export default function FaqPage() {
  const [searchText, setSearchText] = React.useState('');

  const [isOpened, setIsOpened] = React.useState<Set<string>>(new Set());
  const [searchedText, setSearchedText] = React.useState<string | null>(null);

  const handleSearch = React.useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSearchedText(searchText);
  }, [searchText]);

  const toggleVisibility = React.useCallback((key: string) => () => {
    const clonedSet = new Set(isOpened);
    if (!clonedSet.delete(key)) {
      clonedSet.add(key);
    }
    setIsOpened(clonedSet);
  }, [isOpened]);

  return <Layout>
    <HeadTitle
      title="Frequently Asked Questions"
    />
    <Breadcrumbs
      currentPage="FAQ"
    />
    <h2>FAQ (Frequently Asked Questions)</h2>
    <form onSubmit={handleSearch} className="mb-3">
      <div className="row">
        <div className="col pr-0">
          <input
            type="text"
            placeholder="Search FAQ"
            className="form-control border-radius-left-1 border-radius-right-0"
            value={searchText}
            onChange={genericTextChange(setSearchText)}
          />
        </div>
        <div className="col-auto pl-0">
          <button
            className="btn btn-dark-custom border-radius-left-0 border-radius-right-1">
            Search
          </button>
        </div>
      </div>
    </form>

    {faq
      .map(({ title: sectionTitle, items }, sectionIndex) => {
        return (
          <React.Fragment key={sectionIndex}>
            {!searchedText && <h4>{sectionTitle}</h4>}
            {items.map(({ title: sectionItemTitle, content }, itemIndex) => {
              const itemOpenedKey = `${sectionIndex},${itemIndex}`;
              const isItemOpened = isOpened.has(itemOpenedKey);
              if (
                searchedText &&
                searchedText.trim() &&
                !sectionItemTitle.toLowerCase().includes(searchedText.toLowerCase())
                && !getTextFromReactElement(content).toLowerCase().includes(searchedText.toLowerCase())
              ) {
                return null;
              }

              const headerClassName = classNames('border-radius-1', {
                'border-primary': isItemOpened,
                'bg-primary': isItemOpened,
                'bg-light': !isItemOpened,
              })
              const cardClassName = classNames('mb-3 border-radius-1', {
                'border-primary': isItemOpened,
              })
              const titleClassName = classNames('mb-0 d-inline-block align-center', {
                'text-white': isItemOpened,
              });
              const htmlId = kebabCase(sectionItemTitle);

              return (
                <Card key={itemIndex} className={cardClassName}>
                  <Card.Header className={headerClassName} id={htmlId} onClick={toggleVisibility(itemOpenedKey)}>
                    <h5 className={titleClassName}>{sectionItemTitle}</h5>
                    <button className="btn btn-link float-right font-weight-extrabold text-dark"
                    >
                      {isItemOpened ? (
                        <span className="text-white"><AiOutlineUp /></span>
                      ) : (
                        <AiOutlineDown />
                      )}
                    </button>
                  </Card.Header>
                  {isItemOpened && (
                    <Card.Body>
                      {content}
                    </Card.Body>
                  )}
                </Card>
              );
            })}
          </React.Fragment>
        );
      })}


  </Layout>;
}

export { basicGetStaticProps as getStaticProps } from '../lib/static_props';